import { useEffect, useMemo, useState } from "react";
import { FormHelperText, InputAdornment, TextField } from "@mui/material";
import DatePicker from "@components/DatePicker/DatePicker";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { FottServiceType, type TerminalServiceUsageEntry } from "@services/terminal-services/types";
import { convertUaeToUtc, convertUtcToUae } from "@utils/date-time";
import { useTranslation } from "react-i18next";

const LANG_BASE = "terminal-services.details.usage";
const PRESSURE_PATTERN = /^\d{1,3}(\.\d{1,3})?$/;

type UsageFormValues = {
  startTime: string;
  endTime: string;
  pressureInBar: string;
};

type UsageFormErrors = Partial<Record<keyof UsageFormValues | "range", string>>;

export type UsageDrawerSubmitValues = Omit<UsageFormValues, "pressureInBar"> & {
  usageHours: number;
  pressureInBar?: number;
};

export interface UsageDrawerProps {
  isOpen: boolean;
  mode?: "add" | "edit";
  entries: TerminalServiceUsageEntry[];
  initialEntry?: TerminalServiceUsageEntry | null;
  serviceType?: FottServiceType;
  testIdBase: string;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (values: UsageDrawerSubmitValues) => Promise<void> | void;
}

const padDatePart = (value: number) => String(value).padStart(2, "0");

const parseLocalDateTime = (value: string) => {
  const [datePart, timePart] = value.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);

  return new Date(year, month - 1, day, hours, minutes, 0, 0);
};

const toLocalDateTimeValue = (date: Date | null) => {
  if (!date || Number.isNaN(date.getTime())) return "";

  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(
    date.getDate(),
  )}T${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
};

const parseUsageDateTime = (value: string) => {
  if (!value) return null;

  const dateTimeInputValue = toDateTimeInputValue(value);
  const parsedDate = dateTimeInputValue
    ? new Date(convertUaeToUtc(parseLocalDateTime(dateTimeInputValue)))
    : null;

  return parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null;
};

export const toDateTimeInputValue = (value?: string | null) => {
  if (!value) return "";
  if (!value.endsWith("Z") && !value.slice(10).includes("+") && !value.slice(10).includes("-")) {
    return value;
  }

  try {
    return convertUtcToUae(value).slice(0, 16);
  } catch {
    return "";
  }
};

const toDatePickerValue = (value?: string | null) => {
  const dateTimeInputValue = toDateTimeInputValue(value);

  return dateTimeInputValue ? parseLocalDateTime(dateTimeInputValue) : null;
};

const getNextMinute = (date: Date) => {
  const nextMinute = new Date(date);
  nextMinute.setMinutes(nextMinute.getMinutes() + 1);

  return nextMinute;
};

export const toUtcDateTime = (value: string) => convertUaeToUtc(parseLocalDateTime(value));

export const formatLocalDateTime = (value: string) => {
  const [datePart, timePart] = value.split("T");
  const [year, month, day] = datePart.split("-");

  return `${day}/${month}/${year} ${timePart}`;
};

export const calculateUsageHours = (startTime: string, endTime: string) => {
  const startDate = parseUsageDateTime(startTime);
  const endDate = parseUsageDateTime(endTime);

  if (!startDate || !endDate || endDate.getTime() <= startDate.getTime()) {
    return null;
  }

  const totalMinutes = Math.round((endDate.getTime() - startDate.getTime()) / 60000);

  return Math.round((totalMinutes / 60) * 100) / 100;
};

export const formatUsageHours = (value: number) => value.toFixed(2).replace(/\.?0+$/, "");

export const hasUsageOverlap = (
  candidate: UsageFormValues,
  entries: TerminalServiceUsageEntry[],
  excludeId?: string | null,
) => {
  const candidateStart = parseUsageDateTime(candidate.startTime);
  const candidateEnd = parseUsageDateTime(candidate.endTime);

  if (!candidateStart || !candidateEnd) return false;

  return entries.some((entry) => {
    if (entry.id === excludeId) return false;

    const entryStart = parseUsageDateTime(entry.startTime);
    const entryEnd = parseUsageDateTime(entry.endTime);

    if (!entryStart || !entryEnd) return false;

    return (
      candidateStart.getTime() < entryEnd.getTime() && candidateEnd.getTime() > entryStart.getTime()
    );
  });
};

const UsageDrawer = ({
  isOpen,
  mode = "add",
  entries,
  initialEntry,
  serviceType,
  testIdBase,
  isSubmitting = false,
  onClose,
  onSubmit,
}: UsageDrawerProps) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState<UsageFormValues>({
    startTime: "",
    endTime: "",
    pressureInBar: "",
  });
  const [errors, setErrors] = useState<UsageFormErrors>({});
  const editingEntryId = mode === "edit" ? initialEntry?.id : null;
  const shouldShowPressure = serviceType === FottServiceType.NITROGEN_SUPPLY_SERVICE;
  const calculatedUsageHours = useMemo(
    () => calculateUsageHours(formValues.startTime, formValues.endTime),
    [formValues.endTime, formValues.startTime],
  );
  const startDatePickerValue = toDatePickerValue(formValues.startTime);
  const endDatePickerValue = toDatePickerValue(formValues.endTime);
  const minEndDatePickerValue = startDatePickerValue ? getNextMinute(startDatePickerValue) : null;

  useEffect(() => {
    if (!isOpen) return;

    setFormValues({
      startTime: toDateTimeInputValue(initialEntry?.startTime),
      endTime: toDateTimeInputValue(initialEntry?.endTime),
      pressureInBar:
        initialEntry?.pressureInBar === null || initialEntry?.pressureInBar === undefined
          ? ""
          : String(initialEntry.pressureInBar),
    });
    setErrors({});
  }, [initialEntry?.endTime, initialEntry?.pressureInBar, initialEntry?.startTime, isOpen]);

  const getValidationErrors = (
    values: UsageFormValues,
    { includeRequired }: { includeRequired: boolean },
  ) => {
    const nextErrors: UsageFormErrors = {};

    if (includeRequired && !values.startTime) {
      nextErrors.startTime = t(`${LANG_BASE}.validation.start-required`, {
        defaultValue: "Start time is required.",
      });
    }

    if (includeRequired && !values.endTime) {
      nextErrors.endTime = t(`${LANG_BASE}.validation.end-required`, {
        defaultValue: "End time is required.",
      });
    }

    if (shouldShowPressure) {
      const normalizedPressure = values.pressureInBar.trim();

      if (includeRequired && !normalizedPressure) {
        nextErrors.pressureInBar = t(`${LANG_BASE}.validation.pressure-required`, {
          defaultValue: "Pressure is required.",
        });
      } else if (normalizedPressure && !PRESSURE_PATTERN.test(normalizedPressure)) {
        nextErrors.pressureInBar = t(`${LANG_BASE}.validation.pressure-format`, {
          defaultValue: "Pressure must be a valid amount (up to 3 digits and up to 3 decimals).",
        });
      }
    }

    const startDate = values.startTime ? parseUsageDateTime(values.startTime) : null;

    if (startDate && startDate.getTime() >= Date.now()) {
      nextErrors.startTime = t(`${LANG_BASE}.validation.start-in-past`, {
        defaultValue: "Usage hours start time must be in the past.",
      });
    }

    const nextUsageHours = calculateUsageHours(values.startTime, values.endTime);

    if (!nextErrors.startTime && values.startTime && values.endTime && nextUsageHours === null) {
      nextErrors.range = t(`${LANG_BASE}.validation.end-after-start`, {
        defaultValue: "End time must be after start time.",
      });
    }

    if (
      Object.keys(nextErrors).length === 0 &&
      values.startTime &&
      values.endTime &&
      hasUsageOverlap(values, entries, editingEntryId)
    ) {
      nextErrors.range = t(`${LANG_BASE}.validation.overlap`, {
        defaultValue:
          "Usage time overlaps with an existing usage entry. Please enter a start and end date/time that does not conflict with previously added usage periods.",
      });
    }

    return nextErrors;
  };

  const setFieldValue = (field: keyof UsageFormValues, value: string) => {
    const nextValues = { ...formValues, [field]: value };

    setFormValues(nextValues);
    setErrors(getValidationErrors(nextValues, { includeRequired: false }));
  };

  const handleSubmit = async () => {
    const nextErrors = getValidationErrors(formValues, { includeRequired: true });

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0 || calculatedUsageHours === null) {
      return;
    }

    await onSubmit({
      startTime: formValues.startTime,
      endTime: formValues.endTime,
      usageHours: calculatedUsageHours,
      ...(shouldShowPressure ? { pressureInBar: Number(formValues.pressureInBar) } : {}),
    });
  };

  const drawerTitle =
    mode === "edit"
      ? t(`${LANG_BASE}.edit-usage-title`, { defaultValue: "Edit usage" })
      : t(`${LANG_BASE}.add-usage-title`, { defaultValue: "Add usage" });

  return (
    <div data-testid={`${testIdBase}-drawer`} className="w-[400px] max-w-[100vw]">
      <SideDrawer
        isOpen={isOpen}
        title={drawerTitle}
        onClose={onClose}
        onCancel={onClose}
        cancelLabel={t("common.cancel")}
        submitLabel={
          mode === "edit"
            ? t(`${LANG_BASE}.save`, { defaultValue: "Save" })
            : t(`${LANG_BASE}.add`, { defaultValue: "Add usage" })
        }
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isSubmitDisabled={isSubmitting}
      >
        <div className="flex-1 w-[400px] max-w-[100vw] py-2">
          <div className="flex flex-col gap-5 px-6">
            <div className="flex flex-col gap-2">
              <CustomFormLabel>
                {t(`${LANG_BASE}.fields.start-time`, { defaultValue: "Start time" })}
              </CustomFormLabel>
              <DatePicker
                hasError={Boolean(errors.startTime)}
                value={startDatePickerValue}
                onChange={(date) => setFieldValue("startTime", toLocalDateTimeValue(date))}
                testId={`${testIdBase}-start-time-input`}
                timePicker={true}
                combinedInput={true}
                dateConstraint="not-future"
                maxDate={new Date()}
              />
              {errors.startTime && (
                <FormHelperText error sx={{ m: 0 }}>
                  {errors.startTime}
                </FormHelperText>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <CustomFormLabel>
                {t(`${LANG_BASE}.fields.end-time`, { defaultValue: "End time" })}
              </CustomFormLabel>
              <DatePicker
                hasError={Boolean(errors.endTime || errors.range)}
                value={endDatePickerValue}
                onChange={(date) => setFieldValue("endTime", toLocalDateTimeValue(date))}
                testId={`${testIdBase}-end-time-input`}
                timePicker={true}
                combinedInput={true}
                minDate={minEndDatePickerValue ?? undefined}
              />
              {(errors.endTime || errors.range) && (
                <FormHelperText error sx={{ m: 0 }}>
                  {errors.endTime || errors.range}
                </FormHelperText>
              )}
            </div>

            {shouldShowPressure && (
              <div className="flex flex-col gap-2">
                <CustomFormLabel>
                  {t(`${LANG_BASE}.fields.pressure`, { defaultValue: "Pressure" })}
                </CustomFormLabel>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={formValues.pressureInBar}
                  error={Boolean(errors.pressureInBar)}
                  onChange={(event) => setFieldValue("pressureInBar", event.target.value)}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">bar</InputAdornment>,
                    },
                    htmlInput: {
                      "data-testid": `${testIdBase}-pressure-input`,
                      "step": "0.001",
                    },
                  }}
                />
                {errors.pressureInBar && (
                  <FormHelperText error sx={{ m: 0 }}>
                    {errors.pressureInBar}
                  </FormHelperText>
                )}
              </div>
            )}

            <div className="text-sm text-[#31343B]" data-testid={`${testIdBase}-calculated-hours`}>
              {t(`${LANG_BASE}.calculated-hours`, { defaultValue: "Usage hours" })}:{" "}
              <span className="font-semibold">
                {calculatedUsageHours === null ? "-" : formatUsageHours(calculatedUsageHours)}{" "}
                {t(`${LANG_BASE}.hours-suffix`, { defaultValue: "hrs" })}
              </span>
            </div>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default UsageDrawer;
